import * as React from "react";
import axios from "axios";
import { useAccount } from "../../Account";
import {
  Await,
  LoaderFunctionArgs,
  useLoaderData,
  useNavigation,
  useParams,
} from "react-router-dom";
import { UpdateDigitalLoansButton } from "./UpdateDigitalLoansButton";
import { Typography } from "@mui/material";
import { isLoansSortType } from "./HostSystemItemSortControl";
import { HostSystemItemSortType } from "@tlc/ls2pac/dto/HostSystemItemSortType";
import { getValidatedPage } from "./getValidatedPage";
import { GetCurrentLoansReturn } from "@tlc/ls2pac/dto/GetCurrentLoansReturn";
import { HostSystemItemView } from "@tlc/ls2pac/dto/HostSystemItemView";
import { LoansViewData, SharedLoansView } from "./SharedLoansView";
import { LoansSelectionProvider } from "./LoansSelectionProvider";
import { LSResultItemListWithToolbarSkeleton } from "../search/ls/LSResultItem/LSResultItemListWithToolbarSkeleton";
import { LoaderData } from "./MyAccount";

export interface ChildLoansLoaderData extends LoansViewData {
  id: string;
  page: number;
  sort: HostSystemItemSortType;
  pageSize: number;
  totalLoans: number;
  totalLoansOverdue: number;
  loans: HostSystemItemView[];
}

export async function childLoansLoader({
  params,
  request,
}: LoaderFunctionArgs) {
  const pageSize: number = 10;
  const id = params.id!;
  const searchParams = new URL(request.url).searchParams;
  const page = getValidatedPage(searchParams.get("page"));
  const sortParam = searchParams.get("sort");
  const sort =
    sortParam && isLoansSortType(sortParam)
      ? sortParam
      : HostSystemItemSortType.Status;

  const dataPromise = axios
    .get<GetCurrentLoansReturn>(
      `/loans/child/${id}/${page * pageSize - pageSize}/${pageSize}/${sort}`,
    )
    .then(
      ({ data }): ChildLoansLoaderData => ({
        id,
        page,
        sort,
        pageSize,
        totalLoans: data.totalLoans,
        totalLoansOverdue: data.totalLoansOverdue,
        loans: data.loans,
      }),
    )
    .catch((error) => {
      console.error("Error fetching loans", error);
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        throw new Response("Forbidden", { status: 403 });
      }
      throw error;
    });

  return { data: dataPromise, pageSize: pageSize };
}

export const ChildLoansView: React.FC = () => {
  const { data, pageSize } = useLoaderData() as LoaderData<
    typeof childLoansLoader
  >;

  const { patron } = useAccount();

  const navigation = useNavigation();

  return (
    <React.Suspense
      fallback={
        <LSResultItemListWithToolbarSkeleton
          pageSize={pageSize}
          hideCheckbox={true}
          hideActions={true}
        />
      }
    >
      <Await resolve={data}>
        {({ sort, page, pageSize, loans, totalLoans, totalLoansOverdue }) => {
          const items = loans.flatMap((loan: any) =>
            loan.resource ? [loan.resource] : [],
          );
          const { id } = useParams<{ id: string }>();
          const childId = parseInt(id!);
          const child = (patron?.children || []).find((c) => c.id === childId);

          return (
            <>
              {/* // Leaving the provider/associated code for when child loans gets printing/select renew functionality. */}
              {/* // We need it for SharedLoansView anyway */}
              <LoansSelectionProvider
                items={items}
                getId={(resource) => resource.id}
              >
                <SharedLoansView
                  {...data}
                  page={page}
                  sort={sort}
                  loans={loans}
                  totalLoans={totalLoans}
                  totalLoansOverdue={totalLoansOverdue}
                  pageSize={pageSize}
                  displayedLoan={loans}
                  requiresDateRange={false}
                  toolbarActions={
                    <>
                      <Typography sx={{ flex: "1 1" }}></Typography>
                      <UpdateDigitalLoansButton
                        patronId={child?.barcode || ""}
                      />
                    </>
                  }
                  isLoading={navigation.state === "loading"}
                  isStudent={true}
                  patronId={child?.barcode || ""}
                  digitalInformationLastUpdated={undefined}
                />
              </LoansSelectionProvider>
            </>
          );
        }}
      </Await>
    </React.Suspense>
  );
};
